import { Stack, TextField, Typography } from '@mui/material';
import { buildAnrokIntegrationEntityWithMetadata } from '@vy/services';
import { PerUnitProduct } from '@vy/services';
import {
  AnrokProductEntity,
  FixedProduct,
  INTEGRATION_PROVIDERS,
  ProductIntegrationEntity,
  UsageProduct,
} from '@vy/types';
import { isNullOrUndefined, replaceInIndex } from '@vy/utils';
import { FC, useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useUpdateProductIntegrationEntities } from '../../../../../hooks/requests/use-update-product-integration-entities';
import { useIntegrationEntities } from '../../../../../hooks/use-integration-entities';
import { queryKeys } from '../../../../../services/query-key/query-keys';
import { runMutation } from '../../../../../utils/vy-mutation/vy-mutation';

type EditProductAnrokSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
  productName: string;
  product: FixedProduct | PerUnitProduct | UsageProduct;
  contractId: string;
  catalogAnrokId?: string;
  overrideAnrokId?: string;
};

function buildUpdateProductAnrokIntegrationEntitiesPayload(
  integrationEntities: ProductIntegrationEntity[],
  anrokIndex: number,
  anrokEntity: AnrokProductEntity | undefined,
): ProductIntegrationEntity[] {
  if (anrokIndex === -1) {
    if (isNullOrUndefined(anrokEntity)) {
      return integrationEntities;
    }

    return [...integrationEntities, anrokEntity];
  }

  if (isNullOrUndefined(anrokEntity)) {
    return integrationEntities.filter((entity) => entity.integrationProvider !== INTEGRATION_PROVIDERS.anrok);
  }

  return replaceInIndex(integrationEntities, anrokIndex, anrokEntity);
}

export const EditProductAnrokSettingsDialog: FC<EditProductAnrokSettingsDialogProps> = ({
  open,
  onClose,
  productName,
  product,
  contractId,
  catalogAnrokId,
  overrideAnrokId,
}) => {
  const queryClient = useQueryClient();

  const { integrationEntityIndex: anrokIndex, integrationEntities: rawIntegrationEntities } =
    useIntegrationEntities<AnrokProductEntity>({
      integrationEntities: product.integrationEntities,
      integrationProvider: INTEGRATION_PROVIDERS.anrok,
    });

  const integrationEntities = rawIntegrationEntities as ProductIntegrationEntity[];

  const [productId, setProductId] = useState<string>(overrideAnrokId ?? '');

  useEffect(() => {
    if (open) {
      setProductId(overrideAnrokId ?? '');
    }
  }, [open, overrideAnrokId]);

  const updateProductMutation = useUpdateProductIntegrationEntities({
    onSuccess: async () => {
      await queryClient.invalidateQueries(queryKeys.contractPage(contractId));
      await queryClient.invalidateQueries(queryKeys.plans(contractId));
    },
  });

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = async () => {
    const entity =
      productId.trim() === '' ? undefined : buildAnrokIntegrationEntityWithMetadata(productId.trim());

    const updatedIntegrationEntities = buildUpdateProductAnrokIntegrationEntitiesPayload(
      integrationEntities,
      anrokIndex,
      entity,
    );

    await runMutation(
      updateProductMutation,
      {
        productId: product.id,
        integrationEntities: updatedIntegrationEntities,
      },
      {
        pending: 'Updating Anrok product settings...',
        success: 'Anrok product settings updated successfully',
        error: 'Failed to update Anrok product settings',
      },
      {
        onSuccess: () => {
          onClose();
        },
      },
    );
  };

  return (
    <VyDialog
      label={productName ?? 'Product'}
      labelDescription="Edit Anrok settings"
      open={open}
      onClose={handleClose}
      ctaText="Confirm"
      ctaDisabled={updateProductMutation.isLoading}
      onSubmit={handleSubmit}
      cancelButtonText="Cancel"
    >
      <Stack sx={{
        gap: 2
      }}>
        <TextField
          label="Anrok Product ID"
          fullWidth
          value={productId}
          placeholder={catalogAnrokId ?? ''}
          onChange={(e) => setProductId(e.target.value)}
          slotProps={{
            inputLabel: {
              shrink: true,
            },

            htmlInput: { 'data-vy': 'contract-page-anrok-dialog-input' }
          }} />
        {catalogAnrokId != null && (
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            Leave empty to inherit from catalog ({catalogAnrokId}).
          </Typography>
        )}
      </Stack>
    </VyDialog>
  );
};
